import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Check, Zap, Crown, Heart, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tiers = [
  {
    name: 'Family',
    price: 'Free',
    period: '',
    description: 'For family members - full access at no cost',
    color: '#4AC6FF',
    icon: Heart,
    features: ['Access all shows & episodes', 'TV Schedule mode', 'Favorites & watchlist', 'Watch history', 'Themed playlists'],
    cta: 'Contact Admin',
    type: 'family'
  },
  {
    name: 'Premium',
    price: '$4.99',
    period: '/month',
    description: 'Full access to the nostalgia vault',
    color: '#FF3860',
    icon: Crown,
    features: ['Everything in Family', 'Unlimited streaming', 'Commercial-free option', 'Priority support', 'Early access to new shows'],
    cta: 'Subscribe Now',
    type: 'premium',
    popular: true
  }
];

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/subscribe/me`, { withCredentials: true })
        .then(r => setSubStatus(r.data))
        .catch(() => {});
    }
  }, [user]);

  const handleSubscribe = async () => {
    if (!user) { navigate('/login'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/subscribe/checkout`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (data.url) window.location.href = data.url;
    } catch (e) {
      alert('Failed to start checkout. Please try again.');
    }
    setLoading(false);
  };

  const hasAccess = subStatus?.has_access;

  return (
    <div className="page-content bg-[#FDFBEC] min-h-screen">
      <div className="pattern-bg" />
      <div className="max-w-5xl mx-auto px-6 md:px-8 lg:px-12 py-16 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF3860]/10 border-2 border-[#FF3860] mb-4">
            <Star size={14} strokeWidth={2.5} className="text-[#FF3860]" />
            <span className="text-sm font-bold text-[#FF3860] font-['IBM_Plex_Mono'] uppercase tracking-wider">Subscription Plans</span>
          </div>
          <h1 className="font-['Fredoka'] text-4xl sm:text-5xl lg:text-6xl font-black text-[#0A0A0A] tracking-tight leading-none mb-4" data-testid="pricing-title">
            Unlock the <span className="text-[#FF3860]">Nostalgia Vault</span>
          </h1>
          <p className="text-base md:text-lg text-[#4B5563] font-['Nunito'] max-w-lg mx-auto">
            Family members watch free. Everyone else gets full access with a Premium subscription.
          </p>
        </div>

        {/* Active status */}
        {hasAccess && (
          <div className="neo-card p-4 mb-8 bg-[#39FF14]/10 border-[#39FF14] text-center" data-testid="active-subscription">
            <div className="flex items-center justify-center gap-2">
              <Check size={20} strokeWidth={2.5} className="text-green-700" />
              <span className="font-['Fredoka'] font-bold text-green-700">
                You have full access! ({subStatus.is_family ? 'Family Member' : subStatus.subscription_status === 'active' ? 'Premium Subscriber' : 'Admin'})
              </span>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {tiers.map(tier => (
            <div
              key={tier.type}
              className={`neo-card p-8 relative ${tier.popular ? 'border-[#FF3860]' : ''}`}
              style={{ backgroundColor: tier.popular ? '#FFF0F3' : 'white' }}
              data-testid={`pricing-card-${tier.type}`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-[#FF3860] text-white text-xs font-bold rounded-full border-2 border-[#0A0A0A] shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]">
                  MOST POPULAR
                </div>
              )}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg border-2 border-[#0A0A0A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]" style={{ backgroundColor: tier.color }}>
                  <tier.icon size={20} strokeWidth={2.5} color={tier.type === 'premium' ? 'white' : '#0A0A0A'} />
                </div>
                <h3 className="font-['Fredoka'] text-2xl font-bold text-[#0A0A0A]">{tier.name}</h3>
              </div>
              <div className="mb-4">
                <span className="font-['Fredoka'] text-4xl font-black text-[#0A0A0A]">{tier.price}</span>
                {tier.period && <span className="text-[#4B5563] font-['Nunito']">{tier.period}</span>}
              </div>
              <p className="text-sm text-[#4B5563] font-['Nunito'] mb-6">{tier.description}</p>
              <ul className="space-y-2 mb-8">
                {tier.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm font-['Nunito']">
                    <Check size={14} strokeWidth={3} style={{ color: tier.color }} />
                    <span className="text-[#0A0A0A]">{f}</span>
                  </li>
                ))}
              </ul>
              {tier.type === 'premium' ? (
                <button
                  onClick={handleSubscribe}
                  disabled={loading || hasAccess}
                  className="neo-btn w-full py-3 rounded-xl bg-[#FF3860] text-white font-bold disabled:opacity-50"
                  data-testid="subscribe-btn"
                >
                  <Zap size={16} strokeWidth={2.5} className="inline mr-1" />
                  {hasAccess ? 'Already Active' : loading ? 'Loading...' : tier.cta}
                </button>
              ) : (
                <div className="text-center py-3 rounded-xl bg-[#F4F4F5] border-2 border-[#0A0A0A]/10 text-sm font-bold text-[#4B5563] font-['Fredoka']">
                  Ask admin to add you as family
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
